import {
    FrequencyUnits,
    IIndicatorTimeSeriesResponse,
    IndicatorTimeSeriesRequest,
} from '@kcftech/prometheustypescriptapiclient';
import { keepPreviousData, skipToken, useQuery } from '@tanstack/react-query';
import { sdClient } from 'contexts/SDKClientContext';
import { useDebounce } from 'custom_hooks/useDebounce';
import { useStickyNumber } from 'custom_hooks/useStickyNumber';
import { convertChartRangeToDateRange } from 'pages/workbench/shared';
import {
    calcTimeSeriesMaxPoints,
    dateRangesAreEqual,
    getIndicatorLineStyle,
    getTimeSeriesChartPrimaryUnit,
    getTimeSeriesIndicatorUnits,
    transformTimeSeriesData,
} from 'pages/workbench/workbench_tabs/trend/timeseries/shared';
import { TrendIndicator } from 'pages/workbench/workbench_tabs/trend/useTrendIndicators';
import { useCallback, useMemo } from 'react';
import { ChartRange, TimeSeriesData, TimeSeriesDataPoint } from 'shared/chart/core/types';
import { isChartRangeEmpty } from 'shared/chart/shared/data';
import { queryClient } from 'utils/query';

const QueryKey = 'timeseries';
const StickWidthThresholdPx = 50;

export function invalidateTrendTimeSeriesData() {
    queryClient.invalidateQueries({
        queryKey: [QueryKey],
    });
}

type BuildTimeSeriesDataOptions = Readonly<{
    results: IIndicatorTimeSeriesResponse[];
    trendIndicators: TrendIndicator[];
    request: IndicatorTimeSeriesRequest;
    primaryIndicatorUnit: string;
    showMinMax: boolean;
}>;

const buildTimeSeriesData = ({
    results,
    trendIndicators,
    request,
    primaryIndicatorUnit,
    showMinMax,
}: BuildTimeSeriesDataOptions): TimeSeriesData[] =>
    results.map<TimeSeriesData>((series) => {
        const trendIndicator = trendIndicators.find(
            ({ indicatorId }) => indicatorId === series.IndicatorId
        );

        if (!trendIndicator) {
            throw new Error(
                `Series in query results not found in selected indicators: ${series.IndicatorId}`
            );
        }

        const { color, indicator } = trendIndicator;

        const data: TimeSeriesData = {
            axis:
                getTimeSeriesIndicatorUnits(indicator) === primaryIndicatorUnit
                    ? 'left'
                    : 'right',
            seriesUnit: series.DisplayUnits || getTimeSeriesIndicatorUnits(indicator),
            seriesMinY: series.OverallMinY,
            seriesMaxY: series.OverallMaxY,
            dataPoints: transformTimeSeriesData({
                indicator,
                request,
                data: series.IndicatorData,
            }).map<TimeSeriesDataPoint>(({ X, AvgY, MinY, MaxY }) => ({
                x: X,
                y: AvgY ?? 0,
                minY: MinY ?? AvgY ?? 0,
                maxY: MaxY ?? AvgY ?? 0,
            })),
            seriesId: series.IndicatorId,
            lineStyles: {
                color,
                curveStyle: getIndicatorLineStyle(indicator),
            },
            showMinMax,
            isDownsampled: series.IsDownsampled,
        };

        return data;
    });

export type UseTrendTimeSeriesDataOptions = Readonly<{
    trendIndicators: TrendIndicator[];
    chartWidth: number;
    range?: ChartRange;
    zoomRange?: ChartRange;
    showMinMax: boolean;
    runningSpeedUnit: FrequencyUnits;
}>;

export function useTrendTimeSeriesData({
    trendIndicators,
    chartWidth,
    range,
    zoomRange,
    showMinMax,
    runningSpeedUnit,
}: UseTrendTimeSeriesDataOptions) {
    const dateRange = useMemo(
        () =>
            range && !isChartRangeEmpty(range)
                ? convertChartRangeToDateRange(range)
                : undefined,
        [range]
    );

    const zoomDateRange = useMemo(
        () =>
            zoomRange && !isChartRangeEmpty(zoomRange)
                ? convertChartRangeToDateRange(zoomRange)
                : undefined,
        [zoomRange]
    );

    const finalZoomDateRange = useMemo(
        () =>
            !dateRange || !zoomDateRange || dateRangesAreEqual(dateRange, zoomDateRange)
                ? undefined
                : zoomDateRange,
        [dateRange, zoomDateRange]
    );

    const debouncedDateRange = useDebounce(dateRange, 500);
    const debouncedZoomDateRange = useDebounce(finalZoomDateRange, 500);

    if (!debouncedDateRange) {
        throw new Error('Missing query date range');
    }

    const indicatorIds = useMemo(
        () => trendIndicators.map(({ indicatorId }) => indicatorId),
        [trendIndicators]
    );

    const stickyWidth = useStickyNumber(chartWidth, StickWidthThresholdPx);

    const maxPoints = useMemo(
        () => calcTimeSeriesMaxPoints(indicatorIds.length, stickyWidth),
        [stickyWidth, indicatorIds.length]
    );

    // If running speed unit is orders, request Hz instead because running speed is always 1 order
    const runningSpeedUnitForRequest = useMemo(
        () =>
            runningSpeedUnit === FrequencyUnits.Order
                ? FrequencyUnits.Hertz
                : runningSpeedUnit,
        [runningSpeedUnit]
    );

    const request = useMemo(
        () =>
            new IndicatorTimeSeriesRequest({
                AddFirstAndLastPoint: true,
                IncludeAvgData: true,
                IncludeMinAndMaxData: showMinMax,
                IndicatorIds: indicatorIds,
                MaxPointsReturned: maxPoints,
                StartDate: debouncedDateRange.startDate,
                EndDate: debouncedDateRange.endDate,
                RunningSpeedUnits: runningSpeedUnitForRequest,
            }),
        [
            indicatorIds,
            maxPoints,
            runningSpeedUnitForRequest,
            showMinMax,
            debouncedDateRange,
        ]
    );

    const zoomedRequest = useMemo(
        () =>
            debouncedZoomDateRange
                ? new IndicatorTimeSeriesRequest({
                      AddFirstAndLastPoint: true,
                      IncludeAvgData: true,
                      IncludeMinAndMaxData: showMinMax,
                      IndicatorIds: indicatorIds,
                      MaxPointsReturned: maxPoints,
                      StartDate: debouncedZoomDateRange.startDate,
                      EndDate: debouncedZoomDateRange.endDate,
                      RunningSpeedUnits: runningSpeedUnitForRequest,
                  })
                : undefined,
        [
            indicatorIds,
            maxPoints,
            runningSpeedUnitForRequest,
            showMinMax,
            debouncedZoomDateRange,
        ]
    );

    const query = useQuery({
        queryKey: [QueryKey, request],
        queryFn: () => sdClient.getIndicatorTimeSeriesData({ body: request }),
        placeholderData: keepPreviousData,
    });

    const zoomedQuery = useQuery({
        queryKey: [QueryKey, zoomedRequest],

        queryFn: zoomedRequest
            ? () => sdClient.getIndicatorTimeSeriesData({ body: zoomedRequest })
            : skipToken,

        placeholderData: keepPreviousData,
    });

    const { queryData, zoomedQueryData } = useMemo(() => {
        if (query.data) {
            const primaryIndicatorUnit = getTimeSeriesChartPrimaryUnit(
                trendIndicators,
                query.data
            );

            const trendIndicatorsIds = new Set(
                trendIndicators.map((indicator) => indicator.indicatorId)
            );

            const mappedQueryData = buildTimeSeriesData({
                primaryIndicatorUnit,
                results: query.data.filter((datum) =>
                    trendIndicatorsIds.has(datum.IndicatorId)
                ),
                request,
                showMinMax,
                trendIndicators,
            });
            return {
                queryData: mappedQueryData,
                zoomedQueryData: zoomedQuery.data
                    ? buildTimeSeriesData({
                          primaryIndicatorUnit,
                          results: zoomedQuery.data.filter((datum) =>
                              trendIndicatorsIds.has(datum.IndicatorId)
                          ),
                          request,
                          showMinMax,
                          trendIndicators,
                      })
                    : mappedQueryData,
            };
        }
        return {};
    }, [query.data, request, showMinMax, trendIndicators, zoomedQuery.data]);

    const refetch = useCallback(() => {
        query.refetch();
        zoomedQuery.refetch();
    }, [query, zoomedQuery]);

    return {
        timeSeriesData: queryData,
        zoomedTimeSeriesData:
            zoomedRequest && zoomedQueryData ? zoomedQueryData : queryData,
        isLoading: query.isFetching || zoomedQuery.isFetching,
        isError: query.isError || zoomedQuery.isError,
        refetch,
    };
}
